"use client";
import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  getSafeCategoryColor,
  getCategoryTextColor,
} from "@/utils/contrastUtils";
import { usePathname } from "next/navigation";
import { BlogPost, BlogCategory } from "@/types/blog";
import { getAllCategories } from "@/utils/blogUtils";

const colorMap: Record<number, string> = {
  1: "#E0F2FE",
  2: "#FEF3C7",
  3: "#E2F7E1",
  4: "#FCE7F3",
  5: "#F3E8FF",
  6: "#FFEDD5",
  7: "#DBEAFE",
  8: "#DCFCE7",
  9: "#FAE8FF",
  10: "#FFE4E6",
};

interface SubCategoryPageProps {
  subCatData: BlogPost[];
  detailCatId: string;
}

const SubCategoryPage: React.FC<SubCategoryPageProps> = ({
  subCatData,
  detailCatId,
}) => {
  const pathname = usePathname();
  const [category, setCategory] = useState<BlogCategory[]>([]);

  const fetchCategory = async () => {
    const respData = await getAllCategories();
    setCategory(respData);
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  // Get subcategory info from the first post
  const subcategoryInfo = subCatData?.[0]?.subcategory_detail;
  const categoryInfo = subCatData?.[0]?.category_detail;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-8 pb-32 pt-20 lg:pt-24">
        {/* Breadcrumb Navigation */}
        <div className="mb-8">
          <nav className="text-sm">
            <ol className="flex items-center space-x-2 text-gray-600">
              <li>
                <Link
                  href="/blog"
                  className="transition-colors hover:text-[#6049E7]"
                >
                  Home
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              {categoryInfo && (
                <>
                  <li>
                    <Link
                      href={`/blog/${categoryInfo.slug}`}
                      className="transition-colors hover:text-[#6049E7]"
                    >
                      {categoryInfo.title}
                    </Link>
                  </li>
                  <li className="text-gray-400">/</li>
                </>
              )}
              <li className="font-medium text-gray-900">
                {subcategoryInfo?.title || "Subcategory"}
              </li>
            </ol>
          </nav>
        </div>

        {/* Header Section */}
        <div className="mb-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="mb-4 text-[32px] font-bold text-gray-900 lg:text-[40px]">
                {subcategoryInfo?.title || "Subcategory"}
              </h1>
              {subcategoryInfo?.description && (
                <p className="max-w-3xl text-[14px] leading-relaxed text-gray-600 lg:text-[16px]">
                  {subcategoryInfo.description}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {category.map((item) => (
                <Link
                  key={item?.title}
                  href={`/blog/${item.slug}`}
                  className={`min-w-[100px] rounded-[4px] border px-4 py-2 text-center text-sm transition-colors duration-200 ${pathname.includes(item.slug)
                      ? "border-[#6049E7] bg-[#6049E7]/5 text-[#6049E7]"
                      : "border-[#717171] bg-white text-[#202020] hover:border-[#6049E7] hover:text-[#6049E7]"
                    }`}
                >
                  {item?.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="space-y-8">
          {subCatData.map((item, index) => (
            <article
              key={`post-${index + 1}`}
              className="group overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all duration-300"
            >
              <div className="flex flex-col lg:flex-row">
                {/* Image Section */}
                <div className="lg:w-1/3">
                  <Link href={`/blog/${item?.slug}`} className="block">
                    <div className="relative h-64 overflow-hidden lg:h-full">
                      <Image
                        src={item?.f_image_url || ""}
                        alt={item?.title || "blog-image"}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        width={400}
                        height={300}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    </div>
                  </Link>
                </div>
                <div className="p-8 lg:w-2/3">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span
                        className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide"
                        style={{
                          backgroundColor: getSafeCategoryColor(
                            colorMap[(index % 10) + 1],
                          ),
                          color: getCategoryTextColor(
                            colorMap[(index % 10) + 1],
                          ),
                        }}
                      >
                        {item?.category_detail?.title || "Post"}
                      </span>
                      {item?.publish_date && (
                        <span className="text-sm text-gray-500">
                          {new Date(item.publish_date).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            },
                          )}
                        </span>
                      )}
                    </div>
                    <div className="hidden items-center text-sm text-gray-500 sm:flex">
                      <svg
                        className="mr-1 h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {Math.ceil((item?.content?.length || 0) / 1000)} min read
                    </div>
                  </div>

                  <Link href={`/blog/${item?.slug}`}>
                    <h3 className="mb-4 line-clamp-2 text-[20px] font-bold text-gray-900 transition-colors group-hover:text-[#6049E7] lg:text-[22px]">
                      {item?.title}
                    </h3>
                  </Link>

                  {item?.meta_description && (
                    <p className="mb-6 line-clamp-3 leading-relaxed text-gray-600">
                      {item.meta_description}
                    </p>
                  )}

                  <div className="flex items-center justify-between">
                    <Link href={`/blog/${item?.slug}`}>
                      <div className="inline-flex items-center font-semibold text-[#6049E7] transition-colors hover:text-[#553ED0]">
                        Read Full Article
                        <svg
                          className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </div>
                    </Link>
                    {item?.creator && item?.category_detail?.title !== "News" && (
                      <div className="flex items-center gap-3">
                        <div
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-600"
                          style={{ color: "#FF3465" }}
                          aria-hidden="true"
                        >
                          {item.creator?.charAt(0) || "A"}
                        </div>
                        <span className="font-medium text-black">
                          {item.creator}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubCategoryPage;
